import type { MutableRefObject, ReactNode } from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";

import {
  AMBIENT_SCRIBE_STATUS_CANCELLED,
  AMBIENT_SCRIBE_STATUS_FAILED,
  AMBIENT_SCRIBE_STATUS_RECORDING,
  AMBIENT_SCRIBE_STATUS_RETRYING
} from "features/medical-scribe/medical-scribe-constants";
import ScribeContextEditor from "features/medical-scribe/paused-edit/scribe-context-editor.component";
import type { AmbientScribeNote } from "services/types/ambient-scribe-note.type";
import type { AmbientScribeTemplateType } from "services/types/ambient-scribe-template.type";
import type { AmbientScribe } from "services/types/ambient-scribe.type";

import type { ScribeDetailQuery } from "./hooks/use-scribe-detail-query.hook";
import type { ScribeDetailTabs } from "./hooks/use-scribe-detail-tabs.hook";
import ScribeDetailPanel, {
  type ScribeGenerationProps
} from "./scribe-detail-panel.component";
import ScribeDetailTabBar from "./scribe-detail-tab-bar.component";

const FILL_COLUMN_SX = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden"
} as const;

/** Failed, retrying or cancelled with no transcript: the microphone most likely captured nothing. */
function isMissingAudio(remote: AmbientScribe | undefined) {
  const status = remote?.status;
  return (
    !remote?.has_transcription &&
    (status === AMBIENT_SCRIBE_STATUS_FAILED ||
      status === AMBIENT_SCRIBE_STATUS_RETRYING ||
      status === AMBIENT_SCRIBE_STATUS_CANCELLED)
  );
}

type ScribeDetailsViewPaneProps = {
  detail: ScribeDetailQuery;
  tabs: ScribeDetailTabs;
  /** The metadata header and player, or nothing when the parent hides it. */
  header: ReactNode;
  duration: string | undefined;
  generation: ScribeGenerationProps;
  templateType: AmbientScribeTemplateType | undefined;
  audiologyReportId: number | undefined;
  soapPrompt: string | undefined;
  soapFailure: AmbientScribeNote | undefined;
  userId: number | undefined;
  flushContextSaveRef: MutableRefObject<(() => Promise<void>) | null>;
};

/** The View Scribe pane: header, the detail tabs and the open tab's content. */
export default function ScribeDetailsViewPane({
  detail,
  tabs,
  header,
  duration,
  generation,
  templateType,
  audiologyReportId,
  soapPrompt,
  soapFailure,
  userId,
  flushContextSaveRef
}: ScribeDetailsViewPaneProps) {
  const { remote, isLoading, isFetching, isError, isAudiologyMode } = detail;
  const { tabView } = tabs;

  return (
    <Box sx={FILL_COLUMN_SX}>
      {header}

      {isMissingAudio(remote) && (
        <Alert severity="warning" sx={{ mt: 3, flexShrink: 0 }}>
          No audio was captured for this recording. Check that your microphone
          is connected and the correct device is selected, then start a new
          recording.
        </Alert>
      )}

      {isLoading && !remote && (
        <Stack alignItems="center" justifyContent="center" py={6}>
          <CircularProgress />
        </Stack>
      )}

      {/*
        Audiology mode adds `is_generating_audiology_summary` to the `?query=`, which a backend
        predating SN-19067 rejects with a 400 that fails the WHOLE read. Without this branch the
        pane renders the header and then nothing at all — the predicted
        flag-flipped-before-the-backend mistake looks like a blank box rather than a fault.
      */}
      {isAudiologyMode && isError && !remote && (
        <Alert
          severity="error"
          sx={{ mt: 3, flexShrink: 0 }}
          data-testid="audiology-scribe-load-error"
        >
          This recording couldn&apos;t be loaded. Please refresh the page, and
          contact Suno support if it keeps happening.
        </Alert>
      )}

      {remote && remote.status !== AMBIENT_SCRIBE_STATUS_RECORDING && (
        <>
          {tabs.visibleDetailTabs.length > 0 && (
            <ScribeDetailTabBar
              remote={remote}
              visibleTabs={tabs.visibleDetailTabs}
              tabView={tabView}
              onTabChange={tabs.setTabView}
              isAudiologyMode={isAudiologyMode}
              audiologyReportId={audiologyReportId}
              isAvsEnabled={detail.isAvsEnabled}
              salesCoachReviewId={tabs.salesCoachReviewId}
            />
          )}

          <Box sx={FILL_COLUMN_SX}>
            {tabs.shouldShowContextTab && Boolean(remote.id) && (
              <Box
                sx={{
                  ...FILL_COLUMN_SX,
                  display: tabView === "context" ? "flex" : "none"
                }}
              >
                <ScribeContextEditor
                  scribeId={Number(remote.id)}
                  customContext={remote.custom_context}
                  hasLinkedChartNote={remote.chart_note?.id != null}
                  patientId={remote.patient?.id}
                  userId={userId}
                  isActive={tabView === "context"}
                  // Gated on the tab actually existing, not on `shouldShowContextTab`: that only
                  // excludes CANCELLED/FAILED, while the label additionally needs
                  // `has_transcription || ambientScribeProcessingEventsEnabled`. The editor is
                  // mounted (hidden) either way, so without this every paused/uploaded scribe
                  // would write on open for a tab that renders no label — and so no dot.
                  syncOnMount={tabs.availableDetailTabs.includes("context")}
                  onFlushSaveRef={flushContextSaveRef}
                />
              </Box>
            )}

            <ScribeDetailPanel
              remote={remote}
              tabView={tabView}
              isLoading={isLoading || isFetching}
              duration={duration}
              generation={generation}
              isAudiologyMode={isAudiologyMode}
              audiologyReportId={audiologyReportId}
              templateType={templateType}
              soapPrompt={soapPrompt}
              soapFailure={soapFailure}
              salesCoachReviewId={tabs.salesCoachReviewId}
            />
          </Box>
        </>
      )}
    </Box>
  );
}
